import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { getAgentAnalytics, getAgentAnalyticsDetail } from "../../../api/kpi";
import { useSelector } from "react-redux";
import { hasSuperAdminAccess } from "../../../utils/roleAccess";
import { useAnalyticsPeriod, PeriodSelector } from "../components/AnalyticsPeriod";
import { DetailModal, ReasonPills } from "../components/AgentAnalyticsPanel";

/**
 * Per-agent analytics dashboard (Monday #5, agent-profile.html mockup).
 *
 * Reached by a super-admin clicking an agent's name in the Agents leaderboard
 * (route analytics/agent/:agentId, gated by SuperAdminRoute in App.js). Reads
 * the single-agent slice of the agent-analytics endpoint (?agent=<id>, which
 * returns that agent's row + the team totals for "vs team avg") plus the
 * existing per-metric detail endpoint for the record tables.
 */

const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}m`;
  if (v >= 1_000) return `$${Math.round(v / 1_000)}k`;
  return `$${Math.round(v)}`;
};
// rates come through as either a 0-1 ratio or a 0-100 percent depending on the
// helper the BE used; normalise defensively so a bar can never overflow.
const asPct = (v) => {
  const n = Number(v || 0);
  const pct = n <= 1 ? n * 100 : n;
  return Math.max(0, Math.min(100, Math.round(pct)));
};

const auDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

const fmtAvg = (n) => (Math.round(Number(n || 0) * 10) / 10).toLocaleString();

// Auto talking points for the 1:1 (Monday #5, Phase 3): rule-based insights
// derived purely from this period's numbers vs the team average, split into
// what's working and what's worth addressing. Each rule is gated on enough
// activity to be meaningful, then the strongest three of each are kept.
const buildTalkingPoints = (agent, totals) => {
  if (!agent) return { good: [], bad: [] };
  const n = totals.agents || 0;
  const avg = (k) => (n ? (totals[k] || 0) / n : 0);
  const rate = (v) => asPct(v);
  const good = [];
  const bad = [];

  // ---- Attribution-driven (CRM-2026 #12975449949): lead with these so the 1:1
  // separates "what Collings gave" from "what the agent generated". ----
  if (agent.agent_appraisals > 0)
    good.push({ t: "Building their own pipeline.", d: `${fmtInt(agent.agent_appraisals)} self-sourced appraisal${agent.agent_appraisals === 1 ? "" : "s"} this period.` });
  if (agent.collings_leads > 0 && rate(agent.collings_lead_to_appraisal_rate) >= 30)
    good.push({ t: "Converting Collings leads well.", d: `${rate(agent.collings_lead_to_appraisal_rate)}% of Collings leads became appraisals.` });
  if (agent.collings_commission > 0)
    good.push({ t: "Collings opportunity is paying off.", d: `${fmtMoney(agent.collings_commission)} of GCI came from Collings-generated leads.` });
  if (agent.total_commission > 0 && (100 - asPct(agent.agency_contribution_pct)) >= 50)
    good.push({ t: "Strong self-generated revenue.", d: `${100 - asPct(agent.agency_contribution_pct)}% of GCI was self-generated.` });

  if (agent.collings_never_contacted > 0)
    bad.push({ t: "Collings leads left untouched.", d: `${fmtInt(agent.collings_never_contacted)} Collings lead${agent.collings_never_contacted === 1 ? "" : "s"} never contacted.` });
  if (agent.collings_leads > 0 && rate(agent.collings_contacted_4h_rate) < 40)
    bad.push({ t: "Slow first response on Collings leads.", d: `Only ${rate(agent.collings_contacted_4h_rate)}% contacted within 4 hours.` });
  if (agent.leads_lost_genuine > 0)
    bad.push({ t: "Valid leads lost.", d: `${fmtInt(agent.leads_lost_genuine)} genuine lead${agent.leads_lost_genuine === 1 ? "" : "s"} marked lost this period.` });

  // ---- What's working ----
  if (agent.rank === 1 && n > 1)
    good.push({ t: "Top of the leaderboard.", d: `Ranked #1 of ${n} agents by commission this period.` });
  if (agent.total_commission > 0 && agent.total_commission >= avg("total_commission") * 1.15)
    good.push({ t: "Commission is ahead of the team.", d: `${fmtMoney(agent.total_commission)} vs ${fmtMoney(avg("total_commission"))} team average.` });
  if (agent.listings_won > 0 && rate(agent.listing_to_sale_rate) >= 80)
    good.push({ t: "Listings that reach market are selling.", d: `Listing → sale conversion is ${rate(agent.listing_to_sale_rate)}%.` });
  if (agent.current_listings > 0 && agent.current_listings >= avg("current_listings") * 1.25)
    good.push({ t: "Larger active pipeline than the team.", d: `${fmtInt(agent.current_listings)} current listings vs ${fmtAvg(avg("current_listings"))} team average.` });
  if (agent.appraisals > 0 && rate(agent.appraisal_to_listing_rate) >= 40 && rate(agent.appraisal_to_listing_rate) >= rate(totals.appraisal_to_listing_rate))
    good.push({ t: "Winning the appraisals pitched.", d: `Appraisal → listing is ${rate(agent.appraisal_to_listing_rate)}%.` });
  if (agent.properties_sold > 0 && agent.properties_sold >= avg("properties_sold") * 1.25)
    good.push({ t: "Selling more than the team.", d: `${fmtInt(agent.properties_sold)} sold vs ${fmtAvg(avg("properties_sold"))} team average.` });
  if (agent.leads_generated > 0 && rate(agent.actioned_24h_rate) >= 60)
    good.push({ t: "Leads are actioned quickly.", d: `${rate(agent.actioned_24h_rate)}% of leads contacted within 24 hours.` });

  // ---- Worth addressing ----
  if (agent.aging_leads > 0)
    bad.push({ t: "Leads aging without contact.", d: `${fmtInt(agent.aging_leads)} lead${agent.aging_leads === 1 ? "" : "s"} untouched past the follow-up window.` });
  if (agent.leads_generated > 0 && rate(agent.actioned_24h_rate) < 40)
    bad.push({ t: "Leads aren't actioned fast enough.", d: `Only ${rate(agent.actioned_24h_rate)}% of leads contacted within 24 hours.` });
  if (agent.leads_generated > 0 && rate(agent.lead_to_database_rate) < rate(totals.lead_to_database_rate))
    bad.push({ t: "Few leads make it into the database.", d: `Lead → database is ${rate(agent.lead_to_database_rate)}%, below the ${rate(totals.lead_to_database_rate)}% team rate.` });
  if (agent.avg_days_on_market > 0 && totals.avg_days_on_market > 0 && agent.avg_days_on_market > totals.avg_days_on_market)
    bad.push({ t: "Listings sit on market longer than the team.", d: `${fmtInt(agent.avg_days_on_market)} days vs ${fmtInt(totals.avg_days_on_market)} team average.` });
  if (agent.appraisals > 0 && rate(agent.appraisal_to_withdrawn_rate) >= 20)
    bad.push({ t: "Appraisals are being withdrawn.", d: `Appraisal → withdrawn is ${rate(agent.appraisal_to_withdrawn_rate)}%.` });
  if (agent.leads_generated > 0 && rate(agent.lead_to_appraisal_rate) < rate(totals.lead_to_appraisal_rate))
    bad.push({ t: "Few leads convert to appraisals.", d: `Lead → appraisal is ${rate(agent.lead_to_appraisal_rate)}%, below the ${rate(totals.lead_to_appraisal_rate)}% team rate.` });

  return { good: good.slice(0, 3), bad: bad.slice(0, 3) };
};

const TalkingColumn = ({ tone, title, items }) => (
  <div>
    <h3 className="text-[13px] font-bold mb-3 flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${tone === "good" ? "bg-[#1a8f56]" : "bg-[#c8392f]"}`} />
      {title}
    </h3>
    {items.length ? items.map((it, i) => (
      <div key={i} className="flex gap-2.5 py-2.5 border-t border-gray-100 first:border-t-0 first:pt-0 text-[13px] leading-relaxed">
        <span className="text-gray-400 font-semibold text-[12px] pt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
        <div>
          <b className="block mb-0.5 text-gray-900">{it.t}</b>
          <span className="text-gray-500">{it.d}</span>
        </div>
      </div>
    )) : (
      <p className="text-[13px] text-gray-400 py-1">Nothing notable this period.</p>
    )}
  </div>
);

const StatusPill = ({ status }) => {
  const s = String(status || "").toLowerCase();
  const tone =
    s.includes("sold") ? "bg-[#e7f6ee] text-[#1a8f56]"
    : s.includes("withdraw") ? "bg-[#fbeae7] text-[#c8392f]"
    : s.includes("offer") || s.includes("contract") ? "bg-[#faf0dc] text-[#b9740a]"
    : "bg-[#e2f5f4] text-[#128f89]";
  return <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${tone}`}>{status || "—"}</span>;
};

// One snapshot tile. With `teamAvg` it shows a "vs team avg" delta; with `sub`
// (a plain string, used for the origin-attribution cards that have no natural
// team benchmark) it shows that instead. `goodWhenHigh=false` flips the colour
// (higher days-on-market is worse). `pct`/`money` format the headline value.
// `onClick` opens the records behind the number (Monday 13225628427: "make all
// numbers clickable to show which properties/data").
const SnapTile = ({ label, value, teamAvg, sub, goodWhenHigh = true, money = false, pct = false, accent = false, onClick }) => {
  const shown = pct ? `${asPct(value)}%` : money ? fmtMoney(value) : fmtInt(value);
  let delta = null;
  if (sub !== undefined) {
    delta = <div className="text-[12px] mt-1.5 text-gray-400">{sub}</div>;
  } else {
    const v = Number(value || 0);
    const avg = Number(teamAvg || 0);
    const higher = v >= avg;
    const good = goodWhenHigh ? higher : !higher;
    const avgShown = money ? fmtMoney(avg) : (Math.round(avg * 10) / 10).toLocaleString();
    delta = (
      <div className={`text-[12px] mt-1.5 font-semibold ${good ? "text-[#1a8f56]" : "text-[#c8392f]"}`}>
        {higher ? "▲" : "▼"} vs {avgShown} team avg
      </div>
    );
  }
  const body = (
    <>
      <div className="text-[12px] text-gray-400 mb-1.5">{label}</div>
      <div className={`text-[22px] font-bold leading-none tabular-nums ${accent ? "text-[#128f89]" : "text-gray-900"}`}>{shown}</div>
      {delta}
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} title="Show the records behind this number"
      className="text-left bg-white px-4 py-3.5 border-r border-b border-gray-200 hover:bg-[#f5f8f8] transition-colors">
      {body}
    </button>
  ) : (
    <div className="bg-white px-4 py-3.5 border-r border-b border-gray-200">{body}</div>
  );
};

const FunnelRow = ({ label, value, goodWhenHigh = true, onClick }) => {
  const pct = asPct(value);
  const tone = goodWhenHigh
    ? (pct >= 60 ? "#1a8f56" : pct >= 30 ? "#b9740a" : "#c8392f")
    : (pct <= 10 ? "#1a8f56" : pct <= 40 ? "#b9740a" : "#c8392f");
  return (
    <div
      className={`flex items-center gap-3 ${onClick ? "cursor-pointer rounded-md hover:bg-[#f5f8f8]" : ""}`}
      onClick={onClick} role={onClick ? "button" : undefined} title={onClick ? "Show the records behind this rate" : undefined}
    >
      <div className="w-[150px] text-[12.5px] text-gray-600 shrink-0">{label}</div>
      <div className="flex-1 h-[26px] bg-[#f5f8f8] rounded-md overflow-hidden">
        <div className="h-full rounded-md" style={{ width: `${pct}%`, background: tone }} />
      </div>
      <div className="w-11 text-right font-bold text-[13px] tabular-nums" style={{ color: tone }}>{pct}%</div>
    </div>
  );
};

// `embedded` drops the card chrome (border / bg / margin) so the table can sit
// inside a parent section without a card-in-a-card look.
const RecordTable = ({ title, sub, columns, state, embedded = false }) => {
  const [showAll, setShowAll] = useState(false);
  const rows = state.rows || [];
  const shown = showAll ? rows : rows.slice(0, 8);
  return (
  <section className={embedded ? "" : "bg-white border border-gray-200 rounded-[10px] p-5 mb-5"}>
    <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
      <div>
        {embedded
          ? <h3 className="text-[13px] font-bold text-gray-800">{title}</h3>
          : <h2 className="text-[16px] font-bold text-gray-900">{title}</h2>}
        {sub && <div className={`${embedded ? "text-[12px]" : "text-[13px]"} text-gray-400 mt-0.5`}>{sub}</div>}
      </div>
    </div>
    {state.loading ? (
      <div className="py-8 flex justify-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
    ) : !state.rows.length ? (
      <p className="text-[13px] text-gray-400 py-4">Nothing in this period.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-[13.5px] border-collapse">
          <thead>
            <tr>{columns.map((c) => (
              <th key={c.key} className={`text-[11.5px] text-gray-400 font-semibold pb-2 px-2.5 border-b border-gray-200 ${c.num ? "text-right" : "text-left"}`}>{c.label}</th>
            ))}</tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={r.id || i}>
                {columns.map((c) => (
                  <td key={c.key} className={`py-2.5 px-2.5 border-b border-gray-100 ${c.num ? "text-right tabular-nums" : ""}`}>
                    {c.render ? c.render(r) : (r[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 8 && (
          <div className="text-center pt-3">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="text-[12.5px] font-semibold text-[#128f89] hover:underline"
            >
              {showAll ? "Show less" : `+ ${rows.length - 8} more`}
            </button>
          </div>
        )}
      </div>
    )}
  </section>
  );
};

// Section-nav jump targets (agent-profile.html mockup). Order matches the
// section order rendered below.
// Christian Gravias, Monday 13225628427: "Overview | Prospecting |
// Communications | Collings Leads | Agent Generated | Appraisals | Funnel |
// Value". Overview keeps the "listings" id so the Dashboard's ?section=listings
// deep link still lands on it.
const SECTIONS = [
  { id: "listings", label: "Overview" },
  { id: "prospecting", label: "Prospecting" },
  { id: "communications", label: "Communications" },
  { id: "collings", label: "Collings Leads" },
  { id: "agentgen", label: "Agent Generated" },
  { id: "appraisals", label: "Appraisals" },
  { id: "funnel", label: "Funnel" },
  { id: "value", label: "Value" },
  { id: "talking", label: "Talking Points" },
];

// The four "why" breakdowns, each pill opening the records behind it.
const WHY_ROWS = [
  { key: "listings_lost_reasons", label: "Why listings were lost", metric: "listings_lost", title: "Listings withdrawn" },
  { key: "appraisals_withdrawn_reasons", label: "Why appraisals were withdrawn", metric: "appraisals_withdrawn", title: "Appraisals withdrawn" },
  { key: "leads_ignored_reasons", label: "Why leads were ignored", metric: "leads_ignored", title: "Leads ignored" },
  { key: "leads_lost_reasons", label: "Why leads were lost", metric: "leads_lost_genuine", title: "Leads lost" },
];

// Prospecting / Communications tiles (Monday 13225628427, Phase 1). Counts come
// back on the single-agent response as `agent.activity`; each tile drills into
// its own metric on the detail endpoint.
const PROSPECTING_TILES = [
  { key: "prospecting_notes", label: "Calls / prospecting notes", metric: "prospecting_notes", tone: "accent" },
  { key: "seller_prospects", label: "Seller prospects contacted", metric: "seller_prospects" },
  { key: "buyer_prospects", label: "Buyer prospects contacted", metric: "buyer_prospects" },
  { key: "followups_added", label: "Follow-ups added", metric: "followups_added" },
  { key: "prospecting_appraisals", label: "Appraisals from prospecting", metric: "prospecting_appraisals", tone: "good" },
];
const COMMS_TILES = [
  { key: "emails_sent", label: "Emails sent", metric: "emails_sent", tone: "accent" },
  { key: "sms_sent", label: "SMS sent", metric: "sms_sent", tone: "accent" },
  { key: "calls_logged", label: "Calls logged", metric: "calls_logged" },
  { key: "followups_completed", label: "Follow-ups completed", metric: "followups_completed", tone: "good" },
  { key: "followups_overdue", label: "Overdue follow-ups", metric: "followups_overdue", tone: "bad" },
];

// Calls-per-day bar chart (DOMA 13307134618, Christian Gravias: measure agents
// on a daily basis). Reads `agent.calls_by_day` = [{date:"YYYY-MM-DD", count}],
// already Melbourne-local and zero-filled by the backend, and sums to the "Calls
// logged" tile. A hand-rolled CSS bar chart to match the rest of this page (no
// extra chart dependency).
const CallsPerDay = ({ data }) => {
  const series = Array.isArray(data) ? data : [];
  if (series.length === 0) return null;
  const total = series.reduce((s, p) => s + (p.count || 0), 0);
  const max = series.reduce((m, p) => Math.max(m, p.count || 0), 0);
  const avg = series.length ? total / series.length : 0;
  // The dates are plain "YYYY-MM-DD" strings (Melbourne day), so format them by
  // splitting rather than via Date() — keeps it out of the agency-timezone rule
  // and avoids a UTC off-by-one.
  const fmtDay = (iso) => {
    const parts = String(iso || "").split("-");
    return parts.length === 3 ? `${parts[2]}/${parts[1]}` : iso;
  };
  return (
    <div className="mt-5 pt-4 border-t border-gray-100">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div className="text-[13px] font-bold text-gray-800">Calls per day</div>
        <div className="text-[12px] text-gray-500">
          {fmtInt(total)} calls · <b className="text-gray-800">{avg.toFixed(1)}</b>/day avg
        </div>
      </div>
      {total === 0 ? (
        <div className="text-[13px] text-gray-400">No calls logged in this period.</div>
      ) : (
        <>
          <div className="flex items-end gap-[3px] h-28">
            {series.map((p, i) => {
              const h = max ? Math.round(((p.count || 0) / max) * 100) : 0;
              return (
                <div
                  key={p.date || i}
                  className="flex-1 flex flex-col justify-end h-full group"
                  title={`${fmtDay(p.date)}: ${p.count} call${p.count === 1 ? "" : "s"}`}
                >
                  <div
                    className="w-full rounded-t bg-teal-400 group-hover:bg-teal-500 transition-colors"
                    style={{ height: `${h}%`, minHeight: p.count ? 2 : 0 }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-gray-400">
            <span>{fmtDay(series[0].date)}</span>
            <span>{fmtDay(series[series.length - 1].date)}</span>
          </div>
        </>
      )}
    </div>
  );
};

// Origin pill for the appraisal / sold drill-downs. Colour-codes the four
// buckets that matter (Collings vs agent vs database vs referral/other).
const OriginPill = ({ origin, label }) => {
  const o = String(origin || "");
  if (!o) return <span className="text-gray-300">—</span>;
  const tone =
    o === "collings_generated" ? "bg-[#e2f5f4] text-[#0d6e6a]"
    : o === "agent_generated" ? "bg-[#eef2ff] text-[#4f46e5]"
    : o === "database_nurture" ? "bg-[#faf0dc] text-[#b9740a]"
    : "bg-gray-100 text-gray-500";
  return <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${tone}`}>{label || o}</span>;
};

// A yes/no contact marker for the Collings-leads table.
const ContactMark = ({ ok }) =>
  ok
    ? <span className="text-[#1a8f56] font-semibold text-[12px]">Contacted</span>
    : <span className="text-[#c8392f] font-semibold text-[12px]">Not contacted</span>;

// A single count chip for a section-header breakdown strip.
const CountChip = ({ label, value, tone = "default", onClick }) => {
  const cls =
    tone === "good" ? "text-[#1a8f56]"
    : tone === "bad" ? "text-[#c8392f]"
    : tone === "accent" ? "text-[#128f89]"
    : "text-gray-900";
  const inner = (
    <>
      <div className={`text-[18px] font-bold tabular-nums leading-none ${cls}`}>{fmtInt(value)}</div>
      <div className="text-[11px] text-gray-500 mt-1">{label}</div>
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} title={`Show the ${label.toLowerCase()} records`}
      className="text-left px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 min-w-[92px] hover:border-[#128f89] hover:bg-[#f5f8f8] transition-colors">
      {inner}
    </button>
  ) : (
    <div className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 min-w-[92px]">{inner}</div>
  );
};

export default function AgentDashboardPage({ mine = false }) {
  const { agentId } = useParams();
  const currentUser = useSelector((state) => state.auth?.user);
  const isSuperAdmin = hasSuperAdminAccess(currentUser);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const period = useAnalyticsPeriod();
  const { range } = period;
  const [segment, setSegment] = useState("sales");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState({}); // metric -> {loading, rows}
  // Roster for the super-admin "My Performance" agent switcher (#12986007080),
  // so an admin can toggle between agents instead of returning to the leaderboard.
  const [agentList, setAgentList] = useState([]);

  useEffect(() => {
    if (mine) return;
    // Source the switcher from the analytics leaderboard (the agents that
    // actually have a dashboard for the active segment), not the full user
    // roster. The roster included dev/admin/system accounts with no analytics
    // row, and the landing route jumped to the alphabetically-first one — a dev
    // "Admin Support" account — so the page opened blank (#12986007080 revision).
    getAgentAnalytics({ segment, start_date: range.start, end_date: range.end })
      .then((res) => {
        const list = (res.data?.agents || [])
          // the leaderboard rows carry the name as `agent_name` (matching
          // AgentAnalyticsPanel's own switcher); reading `name` left the list
          // empty, so the landing route never redirected and /analytics/agent
          // sat on a spinner.
          .map((a) => ({ id: a.agent_id, name: (a.agent_name || "").trim() }))
          .filter((a) => a.id && a.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setAgentList(list);
        // Landing route (/analytics/agent, no id) — jump to the first agent that
        // has data so the dashboard + switcher open on a real selection.
        // A team member opens on their own row: with a team, My Performance is
        // hidden from the sidebar and this page replaces it (CRM-2026-00107).
        if (!agentId && list.length) {
          const self = !isSuperAdmin && list.find((a) => String(a.id) === String(currentUser?.id));
          navigate(`/analytics/agent/${(self || list[0]).id}`, { replace: true });
        }
      })
      .catch(() => setAgentList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine, segment, range.start, range.end]);

  // In "My Performance" mode the caller's own id isn't in the URL — it comes back
  // on the analytics response (which uses ?mine=1). The detail tables then key
  // off that resolved id (the BE lets an agent read their own detail rows).
  const agent = data?.agent || null;
  const detailId = mine ? agent?.agent_id : agentId;

  const loadDetail = useCallback((metric, id) => {
    if (!id) return;
    setTables((t) => ({ ...t, [metric]: { loading: true, rows: [] } }));
    getAgentAnalyticsDetail({ agent: id, metric, start_date: range.start, end_date: range.end, segment })
      .then((res) => setTables((t) => ({ ...t, [metric]: { loading: false, rows: res.data?.results || [] } })))
      .catch(() => setTables((t) => ({ ...t, [metric]: { loading: false, rows: [] } })));
  }, [range.start, range.end, segment]);

  useEffect(() => {
    // Landing route with no agent chosen yet — hold the spinner while the roster
    // loader above redirects to the first agent, rather than firing a bad request.
    if (!mine && !agentId) { setLoading(true); return; }
    setLoading(true);
    const params = mine
      ? { mine: 1, activity: 1, start_date: range.start, end_date: range.end, segment }
      : { agent: agentId, activity: 1, start_date: range.start, end_date: range.end, segment };
    getAgentAnalytics(params)
      .then((res) => setData(res.data))
      .catch((err) => {
        toast.error(err?.response?.status === 403 ? "You do not have access to this dashboard." : "Could not load this dashboard.");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [mine, agentId, range.start, range.end, segment]);

  useEffect(() => {
    if (!detailId) return;
    ["current_listings", "listings_won", "collings_leads", "agent_leads", "appraisals", "commissions"].forEach((m) => loadDetail(m, detailId));
  }, [detailId, loadDetail]);

  // Drill-down pop-up behind every number on the page (Monday 13225628427).
  // `rows` given = show those already-loaded rows (so the list always matches
  // the chip it came from); otherwise fetch the metric's rows from the BE.
  const [drill, setDrill] = useState({ open: false, title: "", loading: false, rows: [], metric: "" });
  const openDrill = useCallback((metric, title, extra = {}, rows = null) => {
    const name = agent?.agent_name ? ` · ${agent.agent_name}` : "";
    if (rows) {
      setDrill({ open: true, title: `${title}${name}`, loading: false, rows, metric });
      return;
    }
    if (!detailId) return;
    setDrill({ open: true, title: `${title}${name}`, loading: true, rows: [], metric });
    getAgentAnalyticsDetail({ agent: detailId, metric, start_date: range.start, end_date: range.end, segment, ...extra })
      .then((res) => setDrill((d) => ({ ...d, loading: false, rows: res.data?.results || [] })))
      .catch(() => {
        toast.error("Could not load these records.");
        setDrill((d) => ({ ...d, loading: false }));
      });
  }, [agent, detailId, range.start, range.end, segment]);
  const closeDrill = () => setDrill((d) => ({ ...d, open: false }));
  const [listTab, setListTab] = useState("for_sale");

  // Section-nav active highlighting: track whichever section is in view.
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  useEffect(() => {
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!els.length) return undefined;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); }),
      { rootMargin: "-120px 0px -70% 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [loading, data]);

  // Jump to a section. Offset by the sticky section-nav height so the heading
  // isn't hidden behind it. Uses a plain (instant) window scroll: native smooth
  // scrolling is a no-op in some embedded/reduced-motion contexts, so this is
  // the one behaviour that lands reliably everywhere.
  const goToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    setActiveSection(id); // immediate feedback; the observer refines on scroll
    window.scrollTo(0, el.getBoundingClientRect().top + window.scrollY - 16);
  };

  // Deep-link: a dashboard "My Performance" tile links here with ?section=<id>
  // (CRM-2026-00046), so jump to that section once its content has rendered. Runs
  // after the row data loads (the section elements don't exist before then) and
  // only once per requested section, so a later manual scroll isn't yanked back.
  const jumpedToRef = useRef(null);
  const requestedSection = searchParams.get("section");
  useEffect(() => {
    if (loading || !data) return;
    if (!requestedSection || jumpedToRef.current === requestedSection) return;
    if (!SECTIONS.some((s) => s.id === requestedSection)) return;
    // the section table renders on the same tick as data; defer a tick so the
    // element (and its full height) exists before we measure its offset. A plain
    // timeout (not rAF) so it also fires under automated/headless testing.
    const timer = setTimeout(() => {
      goToSection(requestedSection);
      jumpedToRef.current = requestedSection;
    }, 60);
    return () => clearTimeout(timer);
  }, [loading, data, requestedSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = data?.totals || {};
  const teamN = totals.agents || 0;
  const teamAvg = useMemo(() => {
    const tt = data?.totals || {};
    const n = tt.agents || 0;
    return {
      current_listings: n ? tt.current_listings / n : 0,
      appraisals: n ? tt.appraisals / n : 0,
      leads_generated: n ? tt.leads_generated / n : 0,
      properties_sold: n ? tt.properties_sold / n : 0,
      total_commission: tt.avg_commission_per_agent || 0,
      avg_days_on_market: tt.avg_days_on_market || 0,
    };
  }, [data]);

  if (loading) {
    return <div className="p-12 flex justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (!agent) {
    return (
      <div className="p-8">
        <button onClick={() => navigate(mine ? "/dashboard" : "/analytics")} className="text-sm text-gray-500 flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> {mine ? "Back to Dashboard" : "Back to Agent analytics"}</button>
        <p className="text-gray-500">{mine ? "No performance data for you in the selected period." : "No data for this agent in the selected period."}</p>
      </div>
    );
  }

  const t = tables;
  const talking = buildTalkingPoints(agent, totals);

  // Collings-lead outcome breakdown, computed from the drill-down rows so the
  // header chips always reconcile with the table below them (received ->
  // contacted -> outcome). Falls back to the server counts before rows load.
  const clRows = (t.collings_leads && t.collings_leads.rows) || [];
  const clBreak = {
    received: clRows.length || Number(agent.collings_leads || 0),
    contacted: clRows.filter((r) => r.contacted).length,
    appraisal: clRows.filter((r) => r.outcome === "Appraisal added").length,
    database: clRows.filter((r) => r.outcome === "Database / nurture").length,
    ignored: clRows.filter((r) => r.outcome === "Ignored").length,
    lost: clRows.filter((r) => r.outcome === "Lost").length,
    open: clRows.filter((r) => r.outcome === "Open").length,
  };

  // Appraisal rows, and the agent-generated slice of them for the itemised
  // list + outcome tiles in the Agent Generated section.
  const apprRows = (t.appraisals && t.appraisals.rows) || [];
  const agentApprRows = apprRows.filter((r) => r.origin === "agent_generated");
  const agentApprBy = (outcome) => agentApprRows.filter((r) => r.outcome === outcome);
  // Every bucket, so the parts visibly add up to the total (Christian: "how is
  // there 40 appraisals when it says 4 from Collings & 17 from agent?").
  const appraisalSplit = [
    `${fmtInt(agent.collings_appraisals)} Collings`,
    `${fmtInt(agent.agent_appraisals)} agent`,
    agent.database_appraisals ? `${fmtInt(agent.database_appraisals)} database/referral` : null,
    agent.unknown_appraisals ? `${fmtInt(agent.unknown_appraisals)} unattributed` : null,
  ].filter(Boolean).join(" · ");
  const activity = agent.activity || {};

  const listingColumns = [
    { key: "address", label: "Property", render: (r) => <span className="font-semibold">{r.address}</span> },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
    { key: "price", label: "List price", num: true, render: (r) => (r.price ? fmtMoney(r.price) : "—") },
  ];
  const appraisalColumns = [
    { key: "address", label: "Property", render: (r) => <span className="font-semibold">{r.address}</span> },
    { key: "source", label: "Source", render: (r) => r.source_label || r.source || "—" },
    { key: "date", label: "Date", render: (r) => auDate(r.date) },
    { key: "outcome", label: "Outcome", render: (r) => <StatusPill status={r.outcome || r.status} /> },
    { key: "price", label: "Est. value", num: true, render: (r) => (r.price ? fmtMoney(r.price) : "—") },
  ];
  return (
    <div className="max-w-[1180px] mx-auto px-6 pb-20">
      {/* top bar */}
      <div className="flex items-center gap-4 flex-wrap py-4 border-b border-gray-200 -mx-6 px-6 mb-1">
        {/* A team lead has no Analytics page to go back to (Monday 13225574145). */}
        <button onClick={() => navigate(mine || !isSuperAdmin ? "/dashboard" : "/analytics")} className="text-[13px] text-gray-400 flex items-center gap-1.5 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> {mine || !isSuperAdmin ? "Dashboard" : "Agent analytics"}
        </button>
        <span className="text-gray-300">/</span>
        <b className="text-[13px] text-gray-800">{mine ? "My Performance" : agent.agent_name}</b>
        <div className="flex-1" />
        {/* Agent switcher — toggle between agents without going back to the
            leaderboard (#12986007080). Super-admin per-agent view only. */}
        {!mine && agentList.length > 0 && (
          <select
            value={String(agentId || "")}
            onChange={(e) => navigate(`/analytics/agent/${e.target.value}`)}
            title="Switch agent"
            className="text-[13px] border border-gray-300 rounded-lg px-3 py-1.5 bg-white max-w-[200px]"
          >
            {agentList.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )}
        <PeriodSelector period={period} />
        <div className="flex border border-gray-200 rounded-lg overflow-hidden">
          {["sales", "rentals"].map((s) => (
            <button key={s} onClick={() => setSegment(s)}
              className={`px-3.5 py-1.5 text-[13px] font-medium capitalize ${segment === s ? "bg-[#128f89] text-white" : "bg-white text-gray-500"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* header */}
      <div className="flex items-end gap-5 flex-wrap py-8">
        <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-[22px] font-bold shrink-0"
             style={{ background: "linear-gradient(135deg,#1cb2ab,#0d6e6a)" }}>
          {initials(agent.agent_name)}
        </div>
        <div>
          <h1 className="text-[26px] font-bold text-gray-900 tracking-tight mb-1">{agent.agent_name}</h1>
          <div className="flex gap-2.5 flex-wrap items-center text-[13px] text-gray-500">
            {agent.rank ? <span className="bg-[#e2f5f4] text-[#128f89] rounded px-2 py-0.5 font-semibold text-[12px]">#{agent.rank} of {teamN} agents</span> : null}
            <span>Sales Agent · Collings Real Estate</span>
          </div>
        </div>
      </div>

      {/* snapshot — restructured to separate Collings-generated opportunity from
          what the agent sourced themselves (CRM-2026 #12975449949). Every tile
          opens the records behind it (Monday 13225628427). */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-gray-200 rounded-[10px] overflow-hidden mb-7">
        <SnapTile label="Current listings" value={agent.current_listings} teamAvg={teamAvg.current_listings}
          onClick={() => openDrill("current_listings", "Current listings")} />
        <SnapTile label="Collings leads received" value={agent.collings_leads}
          sub={`${asPct(agent.collings_lead_to_appraisal_rate)}% → appraisal`}
          onClick={() => openDrill("collings_leads", "Collings leads received")} />
        {/* Was "Agent opportunities generated" (agent-created LEADS), which read
            0 next to 17 agent appraisals. Agents add appraisals, not leads, so
            this now counts the appraisals they generated. */}
        <SnapTile label="Agent appraisals generated" value={agent.agent_appraisals}
          sub={`of ${fmtInt(agent.appraisals)} appraisals added`}
          onClick={() => openDrill("appraisals", "Agent appraisals generated", { origin: "agent_generated" })} />
        <SnapTile label="Appraisals added" value={agent.appraisals}
          sub={appraisalSplit}
          onClick={() => openDrill("appraisals", "Appraisals added")} />
        <SnapTile label="Sold" value={agent.properties_sold} teamAvg={teamAvg.properties_sold}
          onClick={() => openDrill("commissions", "Sold")} />
        <SnapTile label="Total commission" value={agent.total_commission}
          sub={`${fmtMoney(agent.collings_commission)} Collings · ${fmtMoney(agent.agent_commission)} agent`} money
          onClick={() => openDrill("commissions", "Commission")} />
        <SnapTile label="Avg days on market" value={agent.avg_days_on_market} teamAvg={teamAvg.avg_days_on_market} goodWhenHigh={false}
          onClick={() => openDrill("current_listings", "Days on market · current listings")} />
        <SnapTile label="Agency contribution" value={agent.agency_contribution_pct}
          sub="Collings GCI ÷ total GCI" pct accent
          onClick={() => openDrill("commissions", "Agency contribution · sold")} />
      </div>

      {/* section nav — jump to each section (agent-profile.html mockup). Not
          sticky: the app's overflow-x-hidden content wrapper breaks position:
          sticky, so a static bar is used instead. */}
      <div className="flex gap-1 bg-white border border-gray-200 rounded-[10px] p-1 mb-7 overflow-x-auto">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => goToSection(s.id)}
            className={`px-4 py-2 text-[13px] font-semibold rounded-md whitespace-nowrap ${activeSection === s.id ? "bg-[#f5f8f8] text-gray-900" : "text-gray-500 hover:text-gray-800"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Overview — For Sale Now, plus a "New Properties Listed for Sale" tab in
          the same format (Monday 13225628427), then the four "why" breakdowns. */}
      <section id="listings" className="scroll-mt-24 bg-white border border-gray-200 rounded-[10px] p-5 mb-5">
        <div className="flex gap-1 border-b border-gray-200 mb-4 -mt-1 overflow-x-auto">
          {[
            { id: "for_sale", label: "For Sale Now", n: agent.current_listings },
            { id: "new_listed", label: "New Properties Listed for Sale", n: agent.listings_won },
          ].map((tab) => (
            <button key={tab.id} type="button" onClick={() => setListTab(tab.id)}
              className={`px-3 py-2 text-[13.5px] font-semibold whitespace-nowrap border-b-2 -mb-px ${listTab === tab.id ? "border-[#128f89] text-gray-900" : "border-transparent text-gray-500 hover:text-gray-800"}`}>
              {tab.label} <span className="text-gray-400 font-medium">{fmtInt(tab.n)}</span>
            </button>
          ))}
        </div>
        {listTab === "for_sale" ? (
          <RecordTable
            key="for_sale"
            title="For Sale Now" sub={`${fmtInt(agent.current_listings)} current listings`}
            state={t.current_listings || { loading: true, rows: [] }}
            columns={listingColumns}
            embedded
          />
        ) : (
          <RecordTable
            key="new_listed"
            title="New Properties Listed for Sale" sub={`${fmtInt(agent.listings_won)} listed this period`}
            state={t.listings_won || { loading: true, rows: [] }}
            columns={listingColumns}
            embedded
          />
        )}

        <div className="mt-5 pt-4 border-t border-gray-100 flex flex-col gap-2.5">
          {WHY_ROWS.map((w) => {
            const reasons = agent[w.key] || [];
            const isLead = w.metric.startsWith("leads");
            return (
              <div key={w.key} className="flex items-center flex-wrap gap-2 text-[12px]">
                <span className="text-gray-500 font-medium uppercase tracking-wide w-full sm:w-[230px] shrink-0">{w.label}:</span>
                {reasons.length ? (
                  <ReasonPills
                    reasons={reasons}
                    onPick={(reason) => openDrill(w.metric, `${w.title} · ${reason}`, { reason })}
                    noun={isLead ? "lead" : "property"}
                    nounPlural={isLead ? "leads" : "properties"}
                  />
                ) : <span className="text-gray-400">None this period</span>}
              </div>
            );
          })}
        </div>
      </section>

      {/* Prospecting — call / prospecting notes the agent logged (Monday 13225628427). */}
      <section id="prospecting" className="scroll-mt-24 bg-white border border-gray-200 rounded-[10px] p-5 mb-5">
        <h2 className="text-[16px] font-bold text-gray-900">Prospecting</h2>
        <div className="text-[13px] text-gray-400 mt-0.5 mb-4">Calls and notes the agent logged against potential sellers and buyers</div>
        <div className="flex gap-2.5 flex-wrap">
          {PROSPECTING_TILES.map((c) => (
            <CountChip key={c.key} label={c.label} value={activity[c.key]} tone={c.tone}
              onClick={() => openDrill(c.metric, c.label, c.params)} />
          ))}
        </div>
      </section>

      {/* Communications — what the agent sent and logged through the CRM. */}
      <section id="communications" className="scroll-mt-24 bg-white border border-gray-200 rounded-[10px] p-5 mb-5">
        <h2 className="text-[16px] font-bold text-gray-900">Communications</h2>
        <div className="text-[13px] text-gray-400 mt-0.5 mb-4">Emails, texts, calls and follow-ups through the CRM</div>
        <div className="flex gap-2.5 flex-wrap">
          {COMMS_TILES.map((c) => (
            <CountChip key={c.key} label={c.label} value={activity[c.key]} tone={c.tone}
              onClick={() => openDrill(c.metric, c.label, c.params)} />
          ))}
        </div>
        <CallsPerDay data={agent.calls_by_day} />
      </section>

      {/* Collings Leads — opportunity Collings handed the agent, its outcomes and
          the agent's response time on it (CRM-2026 #12975449949). */}
      <section id="collings" className="scroll-mt-24 bg-white border border-gray-200 rounded-[10px] p-5 mb-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-[16px] font-bold text-gray-900">Collings Leads</h2>
            <div className="text-[13px] text-gray-400 mt-0.5">Leads Collings generated and handed to this agent</div>
          </div>
          <div className="text-right">
            <div className="text-[13px] text-gray-500">Lead → appraisal <b className="text-gray-800">{asPct(agent.collings_lead_to_appraisal_rate)}%</b></div>
            <div className="text-[11px] text-gray-400 mt-0.5">{fmtMoney(agent.collings_commission)} Collings GCI</div>
          </div>
        </div>

        {/* outcome breakdown — reconciles with the table below; each chip opens
            exactly the rows it counts */}
        <div className="flex gap-2.5 flex-wrap mb-5">
          <CountChip label="Received" value={clBreak.received} tone="accent" onClick={() => openDrill("collings_leads", "Collings leads received", {}, clRows)} />
          <CountChip label="Contacted" value={clBreak.contacted} tone="good" onClick={() => openDrill("collings_leads", "Collings leads contacted", {}, clRows.filter((r) => r.contacted))} />
          <CountChip label="Appraisal" value={clBreak.appraisal} tone="good" onClick={() => openDrill("collings_leads", "Collings leads → appraisal", {}, clRows.filter((r) => r.outcome === "Appraisal added"))} />
          <CountChip label="Database" value={clBreak.database} onClick={() => openDrill("collings_leads", "Collings leads → Database", {}, clRows.filter((r) => r.outcome === "Database / nurture"))} />
          <CountChip label="Ignored" value={clBreak.ignored} tone="bad" onClick={() => openDrill("collings_leads", "Collings leads ignored", {}, clRows.filter((r) => r.outcome === "Ignored"))} />
          <CountChip label="Lost" value={clBreak.lost} tone="bad" onClick={() => openDrill("collings_leads", "Collings leads lost", {}, clRows.filter((r) => r.outcome === "Lost"))} />
          <CountChip label="Open" value={clBreak.open} onClick={() => openDrill("collings_leads", "Collings leads still open", {}, clRows.filter((r) => r.outcome === "Open"))} />
        </div>

        {/* response-time accountability on Collings-generated leads */}
        <div className="mb-5 pb-4 border-b border-gray-100">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
            <div className="text-[13px] font-bold text-gray-800">Response time</div>
            <div className="text-[12px] text-gray-500">Avg first response: <b className="text-gray-800">{Number(agent.collings_avg_response_hours || 0)}h</b></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Contacted < 1h", rate: agent.collings_contacted_1h_rate, good: true },
              { label: "Contacted < 4h", rate: agent.collings_contacted_4h_rate, good: true },
              { label: "Contacted < 24h", rate: agent.collings_contacted_24h_rate, good: true },
              { label: "Never contacted", rate: agent.collings_never_contacted_rate, good: false },
            ].map((b) => (
              <button key={b.label} type="button" title="Show the Collings leads"
                onClick={() => (b.good
                  ? openDrill("collings_leads", "Collings leads contacted", {}, clRows.filter((r) => r.contacted))
                  : openDrill("collings_leads", "Collings leads never contacted", {}, clRows.filter((r) => !r.contacted)))}
                className="text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-[#128f89] hover:bg-[#f5f8f8] transition-colors">
                <div className={`text-[18px] font-bold tabular-nums ${b.good ? "text-gray-900" : (asPct(b.rate) > 0 ? "text-[#c8392f]" : "text-gray-900")}`}>{asPct(b.rate)}%</div>
                <div className="text-[11px] text-gray-500">{b.label}</div>
              </button>
            ))}
          </div>
        </div>

        <RecordTable
          title="Collings-generated leads" sub={`${fmtInt(clBreak.received)} received this period`}
          state={t.collings_leads || { loading: true, rows: [] }}
          columns={[
            { key: "property", label: "Lead / property", render: (r) => (
              <div>
                <span className="font-semibold">{r.address || r.name || "—"}</span>
                {r.address && r.name ? <div className="text-[11px] text-gray-400">{r.name}</div> : null}
              </div>
            ) },
            { key: "source", label: "Source", render: (r) => r.source || "—" },
            { key: "stage", label: "Status", render: (r) => r.stage || "—" },
            { key: "contacted", label: "Contacted", render: (r) => <ContactMark ok={r.contacted} /> },
            { key: "outcome", label: "Outcome", render: (r) => r.outcome || "—" },
            { key: "date", label: "Received", num: true, render: (r) => auDate(r.date) },
          ]}
          embedded
        />
      </section>

      {/* Agent Generated — opportunity the agent sourced themselves. Christian
          (Monday 13225628427): "Need to show which appraisals the agents have
          generated. Not just have the number 23. We need an itemised list", plus
          tiles for where they ended up. */}
      <section id="agentgen" className="scroll-mt-24 bg-white border border-gray-200 rounded-[10px] p-5 mb-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-[16px] font-bold text-gray-900">Agent Generated</h2>
            <div className="text-[13px] text-gray-400 mt-0.5">Appraisals and leads this agent sourced themselves (prospecting, referrals, own network)</div>
          </div>
          <div className="text-right">
            <div className="text-[13px] text-gray-500">Appraisal → listing <b className="text-gray-800">{asPct(agent.agent_appraisal_to_listing_rate)}%</b></div>
            <div className="text-[11px] text-gray-400 mt-0.5">{fmtMoney(agent.agent_commission)} agent GCI</div>
          </div>
        </div>
        <div className="flex gap-2.5 flex-wrap mb-5">
          <CountChip label="Appraisals generated" value={agent.agent_appraisals} tone="accent"
            onClick={() => openDrill("appraisals", "Agent appraisals generated", {}, agentApprRows)} />
          <CountChip label="Listed" value={agentApprBy("Listed").length} tone="good"
            onClick={() => openDrill("appraisals", "Agent appraisals → listed", {}, agentApprBy("Listed"))} />
          <CountChip label="Sold" value={agentApprBy("Sold").length} tone="good"
            onClick={() => openDrill("appraisals", "Agent appraisals → sold", {}, agentApprBy("Sold"))} />
          <CountChip label="Still appraisal" value={agentApprBy("Appraisal").length}
            onClick={() => openDrill("appraisals", "Agent appraisals still open", {}, agentApprBy("Appraisal"))} />
          <CountChip label="Withdrawn / lost" value={agentApprBy("Withdrawn").length} tone="bad"
            onClick={() => openDrill("appraisals", "Agent appraisals withdrawn", {}, agentApprBy("Withdrawn"))} />
          <CountChip label="Added to Database" value={agent.agent_leads_to_database}
            onClick={() => openDrill("leads_to_database", "Agent leads added to Database", { origin: "agent_generated" })} />
          <CountChip label="Leads ignored" value={agent.agent_leads_ignored} tone="bad"
            onClick={() => openDrill("leads_ignored", "Agent leads ignored", { origin: "agent_generated" })} />
        </div>
        <RecordTable
          title="Agent-generated appraisals" sub={`${fmtInt(agentApprRows.length)} generated this period`}
          state={{ loading: !t.appraisals || t.appraisals.loading, rows: agentApprRows }}
          columns={appraisalColumns}
          embedded
        />
        {agent.agent_leads > 0 && (
          <div className="mt-6">
            <RecordTable
              title="Agent-generated leads" sub={`${fmtInt(agent.agent_leads)} created this period`}
              state={t.agent_leads || { loading: true, rows: [] }}
              columns={[
                { key: "property", label: "Lead / property", render: (r) => (
                  <div>
                    <span className="font-semibold">{r.address || r.name || "—"}</span>
                    {r.address && r.name ? <div className="text-[11px] text-gray-400">{r.name}</div> : null}
                  </div>
                ) },
                { key: "source", label: "Source", render: (r) => r.source || "—" },
                { key: "stage", label: "Stage", render: (r) => r.stage || "—" },
                { key: "outcome", label: "Outcome", render: (r) => r.outcome || "—" },
                { key: "date", label: "Created", num: true, render: (r) => auDate(r.date) },
              ]}
              embedded
            />
          </div>
        )}
      </section>

      {/* Appraisals — with Origin + Source so Collings vs agent is visible per row. */}
      <div id="appraisals" className="scroll-mt-24">
        <RecordTable
          title="Appraisals"
          sub={`${fmtInt(agent.appraisals)} added · ${appraisalSplit}`}
          state={t.appraisals || { loading: true, rows: [] }}
          columns={[
            appraisalColumns[0],
            { key: "origin", label: "Origin", render: (r) => <OriginPill origin={r.origin} label={r.origin_label} /> },
            ...appraisalColumns.slice(1),
          ]}
        />
      </div>

      {/* Funnel — split Collings-generated vs agent-generated, plus the shared
          listing lifecycle that applies once an appraisal is won. */}
      <section id="funnel" className="scroll-mt-24 bg-white border border-gray-200 rounded-[10px] p-5 mb-5">
        <h2 className="text-[16px] font-bold text-gray-900 mb-1">Funnel</h2>
        <div className="text-[13px] text-gray-400 mb-4">How each opportunity stream converts</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-[13px] font-bold text-gray-800 mb-3">Collings-generated</div>
            <div className="flex flex-col gap-2.5">
              <FunnelRow label="Contacted <24h" value={agent.collings_contacted_24h_rate}
                onClick={() => openDrill("collings_leads", "Collings leads contacted", {}, clRows.filter((r) => r.contacted))} />
              <FunnelRow label="Lead → appraisal" value={agent.collings_lead_to_appraisal_rate}
                onClick={() => openDrill("collings_leads", "Collings leads → appraisal", {}, clRows.filter((r) => r.outcome === "Appraisal added"))} />
            </div>
          </div>
          <div>
            <div className="text-[13px] font-bold text-gray-800 mb-3">Agent-generated</div>
            <div className="flex flex-col gap-2.5">
              <FunnelRow label="Lead → appraisal" value={agent.agent_lead_to_appraisal_rate}
                onClick={() => openDrill("appraisals", "Agent appraisals generated", {}, agentApprRows)} />
              <FunnelRow label="Lead → database" value={agent.lead_to_database_rate}
                onClick={() => openDrill("leads_to_database", "Leads added to Database")} />
            </div>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="text-[13px] font-bold text-gray-800 mb-3">Listing lifecycle</div>
          <div className="flex flex-col gap-2.5 max-w-[640px]">
            <FunnelRow label="Appraisal → listing" value={agent.appraisal_to_listing_rate}
              onClick={() => openDrill("listings_won", "Listed this period")} />
            <FunnelRow label="Appraisal → withdrawn" value={agent.appraisal_to_withdrawn_rate} goodWhenHigh={false}
              onClick={() => openDrill("appraisals_withdrawn", "Appraisals withdrawn")} />
            <FunnelRow label="Listing → sale" value={agent.listing_to_sale_rate}
              onClick={() => openDrill("commissions", "Sold")} />
          </div>
        </div>
      </section>

      {/* Value — GCI split by origin, with per-row Origin + Source. */}
      <section id="value" className="scroll-mt-24 bg-white border border-gray-200 rounded-[10px] p-5 mb-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-[16px] font-bold text-gray-900">Value</h2>
            <div className="text-[13px] text-gray-400 mt-0.5">{fmtInt(agent.properties_sold)} sold · {fmtMoney(agent.total_commission)} total commission</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
          {[
            { label: "Collings-generated GCI", value: agent.collings_commission, tone: "accent", origins: ["collings_generated"] },
            { label: "Agent-generated GCI", value: agent.agent_commission, tone: "default", origins: ["agent_generated"] },
            { label: "Database / referral GCI", value: agent.database_commission, tone: "default", origins: ["database_nurture", "referral", "other"] },
            { label: "Unattributed GCI", value: agent.unknown_commission, tone: "default", origins: [""] },
          ].map((c) => (
            <button key={c.label} type="button" title="Show the sales behind this figure"
              onClick={() => openDrill("commissions", c.label, {}, ((t.commissions && t.commissions.rows) || []).filter((r) => c.origins.includes(r.origin || "")))}
              className="text-left px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 hover:border-[#128f89] hover:bg-[#f5f8f8] transition-colors">
              <div className={`text-[18px] font-bold tabular-nums leading-none ${c.tone === "accent" ? "text-[#128f89]" : "text-gray-900"}`}>{fmtMoney(c.value)}</div>
              <div className="text-[11px] text-gray-500 mt-1">{c.label}</div>
            </button>
          ))}
        </div>
        <div className="text-[12px] text-gray-500 mb-5">Agency contribution <b className="text-[#128f89]">{asPct(agent.agency_contribution_pct)}%</b> of GCI came from Collings-generated opportunity{Number(agent.unknown_commission) > 0 ? " · unattributed = REA-feed sales with no lead link" : ""}.</div>
        <RecordTable
          title="Sold" sub={`${fmtInt(agent.properties_sold)} deal(s) this period`}
          state={t.commissions || { loading: true, rows: [] }}
          columns={[
            { key: "address", label: "Property", render: (r) => <span className="font-semibold">{r.address}</span> },
            { key: "origin", label: "Origin", render: (r) => <OriginPill origin={r.origin} label={r.origin_label} /> },
            { key: "source", label: "Source", render: (r) => r.source_label || r.source || "—" },
            { key: "price", label: "Sale price", num: true, render: (r) => (r.price ? fmtMoney(r.price) : "—") },
            { key: "commission", label: "Commission", num: true, render: (r) => fmtMoney(r.commission) },
            { key: "date", label: "Sold", num: true, render: (r) => auDate(r.date) },
          ]}
          embedded
        />
      </section>

      {/* Talking points — auto-generated from this period's numbers */}
      <section id="talking" className="scroll-mt-24 bg-white border border-gray-200 rounded-[10px] p-5 mb-5">
        <h2 className="text-[16px] font-bold text-gray-900 mb-1">{mine ? "Your talking points" : "Talking points for the 1:1"}</h2>
        <div className="text-[13px] text-gray-400 mb-4">Generated from this period's numbers</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <TalkingColumn tone="good" title="What's working" items={talking.good} />
          <TalkingColumn tone="bad" title="Worth addressing" items={talking.bad} />
        </div>
      </section>

      <DetailModal
        open={drill.open}
        onClose={closeDrill}
        title={drill.title}
        loading={drill.loading}
        rows={drill.rows}
        metric={drill.metric}
      />
    </div>
  );
}
